import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatIQD, grandTotal, isItemFilled } from "./data/price";
import { deleteProposal, listProposals } from "./proposalApi";
import { proposalStage } from "./InvoicePanel";

const STAGE_COLORS = { pending: "warning", ok: "success", done: "default", warn: "error", danger: "error" };

// تسعيرة (بانتظار رد الزبون/رفض)، عرض فاتورة مشروع (بانتظار الاعتماد/معتمد/تحوّل لطلب)، أو "يدوي"
function StatusCell({ proposal }) {
  const stage = proposalStage(proposal);
  if (!stage) return <Chip size="small" variant="outlined" label="عرض يدوي" />;
  return <Chip size="small" color={STAGE_COLORS[stage.tone]} label={stage.label} />;
}

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB");
};

// المجموع الكلي بنفس حساب صفحة السعر بالعرض نفسه (data/price.js)
const totalOf = (p) => (p.price?.items?.some(isItemFilled) ? `${formatIQD(grandTotal(p.price))} IQD` : "-");

export default function ProposalListPage() {
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem("username") === "admin";
  // تسعيرة المشروع (فاتورة مشروع بدون تفاصيل) محصورة ببنين والمدير
  const canQuote = ["admin", "Baneen@123"].includes(localStorage.getItem("username"));
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    listProposals()
      .then(setProposals)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return proposals;
    return proposals.filter((p) =>
      [p.recipientName, p.price?.project, p.price?.quoteNumber, p.createdByName]
        .some((v) => (v || "").includes(q))
    );
  }, [proposals, search]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteProposal(deleteTarget.id);
      setProposals((list) => list.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      window.alert(`تعذّر حذف العرض: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box dir="rtl" sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <Typography variant="h5" fontWeight={800}>
          📄 عروض الأسعار
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {canQuote && (
            <Button variant="outlined" onClick={() => navigate("/invoice?project=1&mode=quote")} sx={{ color: "#131414", borderColor: "#131414" }}>
              📋 تسعيرة مشروع جديدة
            </Button>
          )}
          <Button variant="contained" onClick={() => navigate("/proposals/new")} sx={{ bgcolor: "#131414", "&:hover": { bgcolor: "#333" } }}>
            + إنشاء عرض جديد
          </Button>
        </Box>
      </Box>

      <TextField
        size="small"
        fullWidth
        placeholder="بحث بالجهة أو المشروع أو رقم العرض..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, bgcolor: "#fff" }}
      />

      {loading ? (
        <Typography color="text.secondary">جارٍ التحميل...</Typography>
      ) : error ? (
        <Box>
          <Typography color="error" mb={1}>
            تعذّر تحميل العروض: {error}
          </Typography>
          <Button onClick={load}>إعادة المحاولة</Button>
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {proposals.length === 0 ? "ماكو عروض بعد - اضغط «إنشاء عرض جديد»" : "ماكو نتائج للبحث"}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 800, bgcolor: "#f6f6f4" } }}>
                <TableCell align="right">الجهة</TableCell>
                <TableCell align="right">المشروع</TableCell>
                <TableCell align="right">رقم العرض</TableCell>
                <TableCell align="right">الحالة</TableCell>
                <TableCell align="right">المجموع الكلي</TableCell>
                <TableCell align="right">أنشأته</TableCell>
                <TableCell align="right">آخر تعديل</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/proposals/${p.id}`)}>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {p.recipientName || "بدون اسم"}
                  </TableCell>
                  <TableCell align="right">{p.price?.project || "-"}</TableCell>
                  <TableCell align="right" dir="ltr">
                    {p.price?.quoteNumber || "-"}
                  </TableCell>
                  <TableCell align="right">
                    <StatusCell proposal={p} />
                  </TableCell>
                  <TableCell align="right" dir="ltr">
                    {totalOf(p)}
                  </TableCell>
                  <TableCell align="right">{p.createdByName || p.createdBy || "-"}</TableCell>
                  <TableCell align="right">
                    {formatDate(p.updatedAt)}
                    {p.updatedByName ? ` — ${p.updatedByName}` : ""}
                  </TableCell>
                  <TableCell align="left" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                    <Button size="small" onClick={() => navigate(`/proposals/${p.id}`)}>
                      فتح
                    </Button>
                    {isAdmin && (
                      <Button size="small" color="error" onClick={() => setDeleteTarget(p)}>
                        حذف
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="حذف عرض السعر"
        description={deleteTarget ? `راح ينحذف عرض «${deleteTarget.recipientName || "بدون اسم"}» نهائياً.` : ""}
        confirmLabel="حذف"
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
